import advectFrag from './shaders/advect.frag.glsl?raw'
import copyFrag from './shaders/copy.frag.glsl?raw'
import flowFrag from './shaders/flow.frag.glsl?raw'
import displayFrag from './shaders/display.frag.glsl?raw'
import paramsFrag from './shaders/params.frag.glsl?raw'
import quadVert from './shaders/quad.vert.glsl?raw'
import seedFrag from './shaders/seed.frag.glsl?raw'
import simFrag from './shaders/sim.frag.glsl?raw'

import { bindTexture, PingPong, Program, RenderTarget, type GL, type TextureFormat } from './glutil'
import { DEFAULT_PRESET, type Preset } from './presets'

export interface EngineOptions {
  /** simulation resolution as a fraction of CSS pixels */
  simScale: number
  /** hard cap on the longest simulation axis */
  maxSimSize: number
  /** floor on the shortest simulation axis */
  minSimSize: number
  /** device pixel ratio cap for the display pass */
  maxDpr: number
}

/** Factor the mask is rendered at before being averaged onto the grid. */
const MASK_SUPERSAMPLE = 3

/**
 * How far the mark's edge is softened, as a fraction of the mark's own size.
 * The bevel the shader builds comes from the gradient of the field, so an
 * edge that climbs in a texel throws a far harder specular than the pattern
 * beside it, which diffusion has given several texels of falloff. Expressed
 * against the mark rather than in texels so it holds at any resolution.
 * Raise it for a softer mark; too far and thin strokes lose their peak.
 */
const MARK_EDGE_SOFTNESS = 0.035

/** Separable [1,2,1] blur, in place. */
function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function blurInPlace(field: Float32Array, width: number, height: number, passes: number): void {
  const temp = new Float32Array(field.length)
  for (let pass = 0; pass < passes; pass++) {
    for (let y = 0; y < height; y++) {
      const row = y * width
      for (let x = 0; x < width; x++) {
        const l = field[row + Math.max(0, x - 1)]!
        const c = field[row + x]!
        const r = field[row + Math.min(width - 1, x + 1)]!
        temp[row + x] = (l + 2 * c + r) * 0.25
      }
    }
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const u0 = temp[Math.max(0, y - 1) * width + x]!
        const c = temp[y * width + x]!
        const d = temp[Math.min(height - 1, y + 1) * width + x]!
        field[y * width + x] = (u0 + 2 * c + d) * 0.25
      }
    }
  }
}

/** Seconds between parameter-field refreshes while it is evolving. */
const PARAMS_INTERVAL = 0.12

export const DEFAULT_OPTIONS: EngineOptions = {
  simScale: 0.58,
  maxSimSize: 1100,
  minSimSize: 160,
  maxDpr: 2,
}

export type MaskMode = 'block' | 'seed'

interface Splat {
  x: number
  y: number
  radius: number
  strength: number
}

/** Thrown when the browser cannot support the simulation at all. */
export class UnsupportedError extends Error {}

function createContext(canvas: HTMLCanvasElement): GL {
  const gl = canvas.getContext('webgl2', {
    alpha: false,
    antialias: false,
    depth: false,
    stencil: false,
    premultipliedAlpha: false,
    preserveDrawingBuffer: false,
    powerPreference: 'high-performance',
    desynchronized: true,
  })
  if (!gl) throw new UnsupportedError('WebGL2 is not available')
  return gl
}

/**
 * Float render targets are an extension even in WebGL2. RG32F is preferred:
 * the concentrations live in [0,1] and half-float's ~3 decimal digits are
 * coarse enough near 1.0 that fine patterns visibly stall.
 */
function detectFormat(gl: GL): TextureFormat {
  // A lost context reports every extension as unavailable, which would
  // otherwise surface here as a bogus "unsupported browser".
  if (gl.isContextLost()) {
    throw new UnsupportedError('WebGL context is lost')
  }
  if (gl.getExtension('EXT_color_buffer_float')) {
    return { internalFormat: gl.RG32F, format: gl.RG, type: gl.FLOAT }
  }
  if (gl.getExtension('EXT_color_buffer_half_float')) {
    return { internalFormat: gl.RG16F, format: gl.RG, type: gl.HALF_FLOAT }
  }
  throw new UnsupportedError('no float-renderable colour buffer available')
}

export class ReactionDiffusionEngine {
  private readonly canvas: HTMLCanvasElement
  private readonly gl: GL
  private readonly format: TextureFormat
  private readonly options: EngineOptions

  private vao!: WebGLVertexArrayObject
  private simProgram!: Program
  private paramsProgram!: Program
  private seedProgram!: Program
  private copyProgram!: Program
  private flowProgram!: Program
  private advectProgram!: Program
  private displayProgram!: Program

  private state: PingPong | null = null
  private params: RenderTarget | null = null
  private flow: RenderTarget | null = null

  /**
   * 'block' holds the masked region empty on every step, so the pattern
   * flows around the shape. 'seed' instead plants it once at reset and lets
   * the reaction grow outward from it.
   */
  private maskMode: MaskMode = 'block'
  private maskSource: CanvasImageSource | null = null
  private maskTexture: WebGLTexture | null = null
  /** logo width as a fraction of the shorter simulation axis */
  private maskCoverage = 0.4
  /** Where the mark sits on the field, in screen terms: 0..1 left-to-right,
      top-to-bottom. Centred unless the layout wants it out from under the
      content. */
  private maskCenterX = 0.5
  private maskCenterY = 0.5

  private preset: Preset
  private simWidth = 0
  private simHeight = 0
  private cssWidth = 0
  private cssHeight = 0
  private dpr = 1

  /** temporary multiplier on the fall, used to fast-forward a prewarm */
  private driftScale = 1
  private paramsDirty = true
  /** elapsed time at which each of the slow fields was last rendered */
  private paramsRenderedAt = -1
  private flowRenderedAt = -1
  private seedOffset = Math.random() * 1000
  private splats: Splat[] = []

  private running = false
  private frameHandle = 0
  private lastFrame = 0
  private elapsed = 0
  private animate = true
  private disposed = false

  private readonly onContextLost: (event: Event) => void
  private readonly onContextRestored: () => void

  constructor(canvas: HTMLCanvasElement, preset: Preset = DEFAULT_PRESET, options: Partial<EngineOptions> = {}) {
    this.canvas = canvas
    this.preset = preset
    this.options = { ...DEFAULT_OPTIONS, ...options }
    this.gl = createContext(canvas)
    this.format = detectFormat(this.gl)

    this.onContextLost = (event: Event) => {
      // Without preventDefault the context is never restored.
      event.preventDefault()
      this.stop()
    }
    this.onContextRestored = () => {
      if (this.disposed) return
      this.buildPrograms()
      this.state = null
      this.params = null
      this.flow = null
      this.maskTexture = null
      this.resize(this.cssWidth, this.cssHeight, this.dpr)
      if (this.animate) this.start()
      else this.renderOnce()
    }

    canvas.addEventListener('webglcontextlost', this.onContextLost)
    canvas.addEventListener('webglcontextrestored', this.onContextRestored)

    this.buildPrograms()
  }

  private buildPrograms(): void {
    const { gl } = this

    const vao = gl.createVertexArray()
    if (!vao) throw new Error('could not allocate vertex array')
    this.vao = vao

    this.simProgram = new Program(gl, quadVert, simFrag, 'sim')
    this.paramsProgram = new Program(gl, quadVert, paramsFrag, 'params')
    this.seedProgram = new Program(gl, quadVert, seedFrag, 'seed')
    this.copyProgram = new Program(gl, quadVert, copyFrag, 'copy')
    this.flowProgram = new Program(gl, quadVert, flowFrag, 'flow')
    this.advectProgram = new Program(gl, quadVert, advectFrag, 'advect')
    this.displayProgram = new Program(gl, quadVert, displayFrag, 'display')

    gl.disable(gl.DEPTH_TEST)
    gl.disable(gl.BLEND)
  }

  private draw(target: RenderTarget | null): void {
    const { gl } = this
    if (target) {
      target.bind()
    } else {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null)
      gl.viewport(0, 0, this.canvas.width, this.canvas.height)
    }
    gl.bindVertexArray(this.vao)
    gl.drawArrays(gl.TRIANGLES, 0, 3)
  }

  private get aspect(): [number, number] {
    return this.simWidth >= this.simHeight
      ? [this.simWidth / this.simHeight, 1]
      : [1, this.simHeight / this.simWidth]
  }

  resize(cssWidth: number, cssHeight: number, dpr: number): void {
    if (this.disposed || cssWidth <= 0 || cssHeight <= 0) return

    this.cssWidth = cssWidth
    this.cssHeight = cssHeight
    this.dpr = dpr

    const ratio = Math.min(dpr, this.options.maxDpr)
    const viewWidth = Math.max(1, Math.round(cssWidth * ratio))
    const viewHeight = Math.max(1, Math.round(cssHeight * ratio))
    if (this.canvas.width !== viewWidth) this.canvas.width = viewWidth
    if (this.canvas.height !== viewHeight) this.canvas.height = viewHeight

    // Sim resolution follows CSS pixels, not device pixels, so pattern
    // feature size stays constant across displays of different densities.
    let width = cssWidth * this.options.simScale
    let height = cssHeight * this.options.simScale

    const longest = Math.max(width, height)
    if (longest > this.options.maxSimSize) {
      const k = this.options.maxSimSize / longest
      width *= k
      height *= k
    }
    const shortest = Math.min(width, height)
    if (shortest < this.options.minSimSize) {
      const k = this.options.minSimSize / shortest
      width *= k
      height *= k
    }

    const nextWidth = Math.max(2, Math.round(width))
    const nextHeight = Math.max(2, Math.round(height))
    if (nextWidth === this.simWidth && nextHeight === this.simHeight && this.state) return

    const previous = this.state
    const previousWidth = this.simWidth
    const previousHeight = this.simHeight

    this.simWidth = nextWidth
    this.simHeight = nextHeight

    this.state = new PingPong(this.gl, nextWidth, nextHeight, this.format)
    this.params?.dispose()
    this.params = new RenderTarget(this.gl, nextWidth, nextHeight, this.format)
    this.flow?.dispose()
    this.flow = new RenderTarget(this.gl, nextWidth, nextHeight, this.format)
    this.flowRenderedAt = -1
    this.paramsDirty = true
    this.rebuildMask()

    if (previous && previousWidth > 0 && previousHeight > 0) {
      // Carry the live pattern across the resize rather than restarting it.
      this.copyProgram.use()
      bindTexture(this.gl, 0, previous.read.texture, this.copyProgram.at('uState'))
      this.draw(this.state.read)
      previous.dispose()
    } else {
      this.reset()
    }
  }

  setPreset(preset: Preset): void {
    // The field is deliberately kept: changing f/k morphs one regime into
    // another, which looks far better than a hard cut. Call reset() for that.
    this.preset = preset
    this.paramsDirty = true
  }

  getPreset(): Preset {
    return this.preset
  }

  /**
   * Supply the image whose dark pixels drive the mask, centred on the field.
   * See maskMode for what the mask then does. Pass null to remove it.
   */
  setMaskImage(
    source: CanvasImageSource | null,
    coverage = this.maskCoverage,
    mode: MaskMode = this.maskMode,
  ): void {
    this.maskSource = source
    this.maskCoverage = coverage
    this.maskMode = mode
    this.rebuildMask()
  }

  /** Resize the mark without re-supplying the image. */
  setMaskCoverage(coverage: number): void {
    if (coverage === this.maskCoverage) return
    this.maskCoverage = coverage
    this.rebuildMask()
  }

  /** Move the mark without re-supplying the image. See maskCenterX. */
  setMaskCenter(x: number, y: number): void {
    if (x === this.maskCenterX && y === this.maskCenterY) return
    this.maskCenterX = x
    this.maskCenterY = y
    this.rebuildMask()
  }

  /**
   * Rasterise the mask at the current simulation resolution. This has to be
   * redone on resize, or the logo would stretch with the grid.
   */
  private rebuildMask(): void {
    const { gl } = this

    if (!this.maskSource || this.simWidth === 0 || this.simHeight === 0) {
      if (this.maskTexture && !this.maskSource) {
        gl.deleteTexture(this.maskTexture)
        this.maskTexture = null
      }
      return
    }

    const width = this.simWidth
    const height = this.simHeight

    // Supersample, then area-average down onto the grid. Point-sampling the
    // artwork gives every edge texel a hard 0 or 1, and that quantisation is
    // what stair-steps once the display interpolates it. Averaging leaves
    // partial coverage on edge texels — which is exactly what the pattern's
    // own diffused edges look like.
    const hi = document.createElement('canvas')
    hi.width = width * MASK_SUPERSAMPLE
    hi.height = height * MASK_SUPERSAMPLE

    const hiCtx = hi.getContext('2d')
    if (!hiCtx) return

    // White is "outside the shape", so a logo with a transparent ground
    // reads the same as one drawn on white.
    hiCtx.fillStyle = '#ffffff'
    hiCtx.fillRect(0, 0, hi.width, hi.height)
    hiCtx.imageSmoothingEnabled = true
    hiCtx.imageSmoothingQuality = 'high'

    const drawn = Math.min(hi.width, hi.height) * this.maskCoverage
    // Kept far enough inside the edge that the mark never crops.
    const half = drawn / 2
    const originX = clamp(hi.width * this.maskCenterX, half, hi.width - half) - half
    const originY = clamp(hi.height * this.maskCenterY, half, hi.height - half) - half
    hiCtx.drawImage(this.maskSource, originX, originY, drawn, drawn)

    const scratch = document.createElement('canvas')
    scratch.width = width
    scratch.height = height

    const ctx = scratch.getContext('2d', { willReadFrequently: true })
    if (!ctx) return
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(hi, 0, 0, width, height)

    const image = ctx.getImageData(0, 0, width, height)
    const data = image.data

    const mark = new Float32Array(width * height)
    for (let p = 0; p < mark.length; p++) {
      const i = p * 4
      const luminance = (data[i]! * 0.299 + data[i + 1]! * 0.587 + data[i + 2]! * 0.114) / 255
      mark[p] = 1 - luminance
    }

    // Soften the edge onto the same sort of profile diffusion would have
    // given it. Each pass adds 0.5 texel² of variance, so the ramp widens as
    // the square root of the count — hence the generous multiplier.
    const markTexels = Math.min(width, height) * this.maskCoverage
    const passes = clamp(Math.round(markTexels * MARK_EDGE_SOFTNESS), 3, 14)
    blurInPlace(mark, width, height, passes)

    // Coverage: the area the mark occupies, inside which the field follows
    // the drawing. Without it, diffusion creeps into the artwork's own
    // channels within a few steps and the shape closes up into a blob.
    // Taken as the mark's inscribed disc, which is exact for a round mark.
    const radius = Math.min(width, height) * this.maskCoverage / 2
    // Same placement as the drawing above, in grid texels.
    const centreX = clamp(width * this.maskCenterX, radius, width - radius)
    const centreY = clamp(height * this.maskCenterY, radius, height - radius)

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const p = y * width + x
        const i = p * 4
        const distance = Math.hypot(x - centreX, y - centreY) / radius
        const coverage = 1 - Math.min(1, Math.max(0, (distance - 0.92) / 0.1))

        data[i] = Math.round(mark[p]! * 255)
        data[i + 1] = Math.round(coverage * 255)
        data[i + 2] = 0
        data[i + 3] = 255
      }
    }
    ctx.putImageData(image, 0, 0)

    if (!this.maskTexture) {
      const texture = gl.createTexture()
      if (!texture) return
      this.maskTexture = texture
    }

    gl.bindTexture(gl.TEXTURE_2D, this.maskTexture)
    // Canvas rows run top-down; framebuffer UVs run bottom-up.
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, scratch)
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.bindTexture(gl.TEXTURE_2D, null)
  }

  reset(): void {
    if (this.disposed || !this.state) return
    const { gl, preset } = this
    this.seedOffset = Math.random() * 1000

    const program = this.seedProgram
    program.use()
    gl.uniform1i(program.at('uMode'), 0)
    gl.uniform2f(program.at('uAspect'), ...this.aspect)
    gl.uniform1f(program.at('uSeed'), this.seedOffset)
    gl.uniform1f(program.at('uDensity'), preset.chemistry.seedDensity)
    gl.uniform1f(program.at('uSeedAmount'), preset.chemistry.seedAmount)
    gl.uniform2f(program.at('uSimSize'), this.simWidth, this.simHeight)

    if (this.maskTexture && this.maskMode === 'seed') {
      bindTexture(gl, 2, this.maskTexture, program.at('uMask'))
      gl.uniform1f(program.at('uMaskAmount'), 1)
    } else {
      gl.uniform1f(program.at('uMaskAmount'), 0)
    }

    this.draw(this.state.read)
  }

  /** Inject B at a point in [0,1] UV space, y up. */
  splat(x: number, y: number, radius = 0.035, strength = 0.55): void {
    if (this.splats.length >= 24) return
    this.splats.push({ x, y, radius, strength })
  }

  /**
   * Develop the pattern before it is shown, bounded in wall time as well as
   * in steps. How many steps a device manages varies enormously, and a fixed
   * count that is instant on a desktop can lock a phone up for seconds.
   */
  settle(maxSteps: number, budgetMs = Infinity, driftScale = 1, chunk = 250): void {
    if (this.disposed || !this.state) return
    // Falling faster covers the same ground in fewer steps. A prewarm that
    // filled the screen at the normal rate would cost thousands of extra
    // passes and stall the page on load.
    this.driftScale = driftScale
    try {
    // Stepping elapsed between chunks keeps the emitter and the parameter
    // field varying, so a prewarm doesn't bake one frozen set of conditions
    // across the whole run.
      const startedAt = performance.now()
      for (let done = 0; done < maxSteps; done += chunk) {
        this.renderParams()
        this.renderFlow()
        this.runSteps(Math.min(chunk, maxSteps - done))
        this.elapsed += chunk / 720

        if (budgetMs !== Infinity) {
          // Draw calls only queue work, so the clock says nothing until the
          // GPU has actually caught up. finish() is a hard stall, which is
          // precisely what makes the budget mean anything here.
          this.gl.finish()
          if (performance.now() - startedAt >= budgetMs) break
        }
      }
    } finally {
      this.driftScale = 1
    }
  }

  setAnimating(animate: boolean): void {
    this.animate = animate
  }

  private renderParams(): void {
    if (!this.params) return
    const { gl } = this
    const { chemistry } = this.preset

    // The field is low-frequency and changes slowly, so re-rendering it at
    // a few frames a second is indistinguishable from doing it every frame
    // and costs a fraction as much.
    const evolving = chemistry.evolve > 0
    const stale = this.elapsed - this.paramsRenderedAt >= PARAMS_INTERVAL
    if (!this.paramsDirty && !(evolving && stale)) return
    this.paramsRenderedAt = this.elapsed

    const program = this.paramsProgram
    program.use()
    gl.uniform2f(program.at('uFeed'), chemistry.f, chemistry.spread[0])
    gl.uniform2f(program.at('uKill'), chemistry.k, chemistry.spread[1])
    gl.uniform1f(program.at('uVariance'), chemistry.variance)
    gl.uniform1f(program.at('uScale'), chemistry.varianceScale)
    gl.uniform1f(program.at('uSeed'), this.seedOffset)
    gl.uniform2f(program.at('uAspect'), ...this.aspect)
    gl.uniform1f(program.at('uEvolve'), this.elapsed * chemistry.evolve)
    this.draw(this.params)

    this.paramsDirty = false
  }

  /** Bake the velocity field. Shares the parameter field's slow cadence. */
  private renderFlow(): void {
    if (!this.flow) return
    const { gl } = this
    const { chemistry } = this.preset

    // Same reasoning as the parameter field: broad, slow-changing, and so
    // not worth recomputing its noise every frame.
    if (this.flowRenderedAt >= 0 && this.elapsed - this.flowRenderedAt < PARAMS_INTERVAL) return
    this.flowRenderedAt = this.elapsed

    const program = this.flowProgram
    program.use()
    gl.uniform2f(program.at('uAspect'), ...this.aspect)
    gl.uniform1f(program.at('uScale'), chemistry.flowScale)
    gl.uniform1f(program.at('uEvolve'), this.elapsed * chemistry.flowEvolve)
    gl.uniform1f(program.at('uSeed'), this.seedOffset * 0.37)
    gl.uniform1f(program.at('uSpeed'), chemistry.drift * this.driftScale)
    gl.uniform1f(program.at('uVary'), chemistry.flowVary)
    gl.uniform1f(program.at('uLateral'), chemistry.flowLateral)
    this.draw(this.flow)
  }

  private applySplats(): void {
    if (this.splats.length === 0 || !this.state) return
    const { gl } = this
    const program = this.seedProgram
    program.use()
    gl.uniform1i(program.at('uMode'), 1)
    gl.uniform1f(program.at('uMaskAmount'), 0)
    gl.uniform2f(program.at('uAspect'), ...this.aspect)

    for (const splat of this.splats) {
      bindTexture(gl, 0, this.state.read.texture, program.at('uState'))
      gl.uniform2f(program.at('uPoint'), splat.x, splat.y)
      gl.uniform1f(program.at('uRadius'), splat.radius)
      gl.uniform1f(program.at('uStrength'), splat.strength)
      this.draw(this.state.write)
      this.state.swap()
    }
    this.splats.length = 0
  }

  private runSteps(count: number): void {
    if (!this.state || !this.params) return
    const { gl } = this
    const { chemistry } = this.preset

    const program = this.simProgram
    program.use()
    gl.uniform2f(program.at('uTexel'), 1 / this.simWidth, 1 / this.simHeight)
    gl.uniform1f(program.at('uDt'), chemistry.dt)
    gl.uniform1f(program.at('uDa'), chemistry.dA)
    gl.uniform1f(program.at('uDb'), chemistry.dB)


    // Fade the top source from its opening strength down to the steady one,
    // so the field fills from above and then sustains itself.
    const fade = chemistry.emitFade > 0
      ? Math.min(1, this.elapsed / chemistry.emitFade)
      : 1
    gl.uniform1f(
      program.at('uEmit'),
      chemistry.emitStart + (chemistry.emit - chemistry.emitStart) * fade,
    )
    gl.uniform1f(program.at('uEmitBand'), chemistry.emitBand)
    gl.uniform1f(program.at('uEmitScale'), chemistry.emitScale)
    gl.uniform1f(program.at('uEmitTime'), this.elapsed * chemistry.emitSpeed)

    bindTexture(gl, 1, this.params.texture, program.at('uParams'))

    const blocking = this.maskTexture && this.maskMode === 'block'
    if (blocking && this.maskTexture) {
      bindTexture(gl, 2, this.maskTexture, program.at('uMask'))
      gl.uniform1f(program.at('uBlock'), 1)
      gl.uniform1f(program.at('uMarkPin'), chemistry.markPin)
      gl.uniform1f(program.at('uMarkLevel'), chemistry.markLevel)
    } else {
      gl.uniform1f(program.at('uBlock'), 0)
    }

    const stateLocation = program.at('uState')

    const advect = this.advectProgram
    const advectState = advect.at('uState')

    for (let i = 0; i < count; i++) {
      // Advect, then react. Two passes per step rather than one, but folding
      // the bilinear taps into the reaction's nine-sample stencil would cost
      // far more than the extra pass does.
      if (this.flow) {
        advect.use()
        gl.uniform2f(advect.at('uTexel'), 1 / this.simWidth, 1 / this.simHeight)
        bindTexture(gl, 0, this.state.read.texture, advectState)
        bindTexture(gl, 1, this.flow.texture, advect.at('uFlow'))
        if (this.maskTexture && this.maskMode === 'block') {
          bindTexture(gl, 2, this.maskTexture, advect.at('uMask'))
          gl.uniform1f(advect.at('uMaskAmount'), 1)
        } else {
          gl.uniform1f(advect.at('uMaskAmount'), 0)
        }
        this.draw(this.state.write)
        this.state.swap()

        program.use()
      }

      bindTexture(gl, 0, this.state.read.texture, stateLocation)
      bindTexture(gl, 1, this.params.texture, program.at('uParams'))
      if (this.maskTexture && this.maskMode === 'block') {
        bindTexture(gl, 2, this.maskTexture, program.at('uMask'))
      }
      this.draw(this.state.write)
      this.state.swap()
    }
  }

  private renderDisplay(): void {
    if (!this.state) return
    const { gl } = this
    const m = this.preset.material

    const program = this.displayProgram
    program.use()
    bindTexture(gl, 0, this.state.read.texture, program.at('uState'))
    gl.uniform2f(program.at('uSimTexel'), 1 / this.simWidth, 1 / this.simHeight)
    gl.uniform2f(program.at('uResolution'), this.canvas.width, this.canvas.height)
    gl.uniform1f(program.at('uTime'), this.elapsed)

    gl.uniform3f(program.at('uMatteAlbedo'), ...m.matteAlbedo)
    gl.uniform1f(program.at('uMatteRough'), m.matteRough)
    gl.uniform3f(program.at('uGlossAlbedo'), ...m.glossAlbedo)
    gl.uniform1f(program.at('uGlossRough'), m.glossRough)
    gl.uniform1f(program.at('uF0'), m.f0)
    gl.uniform2f(program.at('uEdge'), ...m.edge)
    gl.uniform1f(program.at('uRelief'), m.relief)
    gl.uniform1f(program.at('uMicroGrain'), m.microGrain)

    gl.uniform3f(program.at('uAmbient'), ...m.ambient)
    gl.uniform3f(program.at('uKeyDir'), ...m.keyDir)
    gl.uniform3f(program.at('uKeyColor'), ...m.keyColor)
    gl.uniform2f(program.at('uKeySize'), ...m.keySize)
    gl.uniform3f(program.at('uFillDir'), ...m.fillDir)
    gl.uniform3f(program.at('uFillColor'), ...m.fillColor)
    gl.uniform2f(program.at('uFillSize'), ...m.fillSize)
    gl.uniform3f(program.at('uRimWarm'), ...m.rimWarm)
    gl.uniform3f(program.at('uRimCool'), ...m.rimCool)
    gl.uniform1f(program.at('uRimGain'), m.rimGain)
    gl.uniform1f(program.at('uRimFocus'), m.rimFocus)
    gl.uniform1f(program.at('uRimSpread'), m.rimSpread)

    gl.uniform1f(program.at('uSwell'), m.swell)
    gl.uniform1f(program.at('uSwellScale'), m.swellScale)
    gl.uniform1f(program.at('uSwellSpeed'), m.swellSpeed)

    gl.uniform1f(program.at('uExposure'), m.exposure)
    gl.uniform1f(program.at('uVignette'), m.vignette)
    gl.uniform1f(program.at('uGrain'), m.grain)

    this.draw(null)
  }

  /** One simulated + displayed frame. */
  frame(deltaSeconds: number): void {
    if (this.disposed || !this.state || this.gl.isContextLost()) return
    this.elapsed += deltaSeconds
    this.renderParams()
    this.renderFlow()
    this.applySplats()
    this.runSteps(this.preset.chemistry.steps)
    this.renderDisplay()
  }

  /** Draw the current field without advancing it. */
  renderOnce(): void {
    if (this.disposed || this.gl.isContextLost()) return
    this.renderParams()
    this.renderFlow()
    this.renderDisplay()
  }

  start(): void {
    if (this.running || this.disposed) return
    this.running = true
    this.lastFrame = performance.now()

    const tick = (now: number): void => {
      if (!this.running) return
      // Clamp: a backgrounded tab returns a huge delta on resume.
      const delta = Math.min((now - this.lastFrame) / 1000, 1 / 15)
      this.lastFrame = now
      this.frame(delta)
      this.frameHandle = requestAnimationFrame(tick)
    }
    this.frameHandle = requestAnimationFrame(tick)
  }

  stop(): void {
    this.running = false
    if (this.frameHandle) cancelAnimationFrame(this.frameHandle)
    this.frameHandle = 0
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.stop()

    this.canvas.removeEventListener('webglcontextlost', this.onContextLost)
    this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored)

    this.state?.dispose()
    this.params?.dispose()
    this.flow?.dispose()
    this.simProgram.dispose()
    this.paramsProgram.dispose()
    this.seedProgram.dispose()
    this.copyProgram.dispose()
    this.flowProgram.dispose()
    this.advectProgram.dispose()
    this.displayProgram.dispose()
    this.gl.deleteVertexArray(this.vao)
    if (this.maskTexture) this.gl.deleteTexture(this.maskTexture)

    // Deliberately NOT calling WEBGL_lose_context.loseContext(). A canvas
    // hands back the same context object on every getContext() call, so
    // losing it here would poison any later engine mounted on this canvas —
    // which is exactly what StrictMode's mount/unmount/remount does. The
    // deletes above already release the GPU resources.
  }
}
